import { forwardRef, type SelectHTMLAttributes } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

export type SelectSize = 'sm' | 'md' | 'lg' | 'xl';

export type SelectOption = string | { value: string; label: string; disabled?: boolean };

const SIZE_CLASSES: Record<SelectSize, string> = {
  sm: 'h-phx-control-sm text-phx-xs pl-phx-3 pr-[22px]',
  md: 'h-phx-control text-phx-md pl-phx-4 pr-phx-7',
  lg: 'h-phx-control-lg text-phx-md pl-phx-4 pr-phx-7',
  xl: 'h-phx-control-xl text-phx-lg pl-phx-5 pr-phx-8',
};

const BASE_SELECT =
  'phx-select w-full appearance-none bg-phx-surface text-phx-text rounded-phx ' +
  'border border-phx-border-2 cursor-pointer ' +
  'transition-[border-color,box-shadow] ' +
  'hover:border-phx-border-3 ' +
  'focus:outline-none focus:border-phx-primary focus:shadow-phx-focus ' +
  'disabled:bg-phx-surface-3 disabled:text-phx-text-muted disabled:cursor-not-allowed';

const REQUIRED_BG = 'bg-phx-required-tint focus:bg-phx-surface';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: SelectSize;
  options?: SelectOption[];
  placeholder?: string;
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', options, placeholder, invalid, required, className, children, ...rest },
  ref
) {
  return (
    <div className={cn('phx-select-wrap relative inline-flex w-full', className)}>
      <select
        ref={ref}
        data-size={size}
        data-invalid={invalid ? '' : undefined}
        required={required}
        className={cn(
          BASE_SELECT,
          SIZE_CLASSES[size],
          required && REQUIRED_BG,
          invalid && 'border-phx-danger focus:border-phx-danger'
        )}
        {...rest}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options
          ? options.map((option) =>
              typeof option === 'string' ? (
                <option key={option} value={option}>
                  {option}
                </option>
              ) : (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              )
            )
          : children}
      </select>
      <Icon
        name="chevdown"
        size={12}
        className="text-phx-text-muted right-phx-4 pointer-events-none absolute top-1/2 -translate-y-1/2"
      />
    </div>
  );
});
